/**
 * In-memory state retained while navigating between notifications and summaries.
 *
 * React Router unmounts the notifications page while a patient summary is open.
 * These caches preserve the fetched roll-up rows and each date/tab's scroll
 * offset so browser Back restores the worklist instead of rebuilding it.
 */

import type { SortDirection } from "../components/custom-table/types";
import type { Patient, UnreadDocumentCounts } from "../types";

/** Remembered table sort for one notification view (column + direction). */
export interface NotificationSort {
  /** Column key currently sorted by, or `null` when unsorted. */
  sortKey: string | null;
  /** Active sort direction. */
  direction: SortDirection;
}

/** Parts that uniquely identify one server-paginated notification query. */
export interface NotificationQueryKeyParts {
  /** Free-text search forwarded as `query`. */
  searchQuery?: string;
  /** Controlled table column key (frontend). */
  sortKey?: string | null;
  /** Controlled sort direction. */
  sortDirection?: SortDirection;
  /** 0-indexed page requested from the API. */
  page?: number;
}

/**
 * One cached page of notification results.
 *
 * Every number the footer renders is stored here, not just the rows: the page
 * reads this cache instead of refetching when React Router remounts the view,
 * so a value missing from the entry would silently keep the previously fetched
 * page's figure.
 */
export interface NotificationPageCache {
  /** Transformed patient rows for this page. */
  patients: Patient[];
  /** Patients matching the filters across every page. */
  totalCount: number;
  /** Document rows matching the filters across every page. */
  totalDocumentCount: number;
  /** Document rows carried by the pages before this one. */
  documentOffset: number;
}

const pageByQuery = new Map<string, NotificationPageCache>();
const unreadCountsByDate = new Map<string, UnreadDocumentCounts>();
const scrollByView = new Map<string, number>();
const sortByView = new Map<string, NotificationSort>();

/** Default page size for the Notification View (matches backend default). */
export const NOTIFICATION_PAGE_SIZE = 50;

/**
 * Resolve the notification table region that owns worklist scrolling.
 *
 * @returns The table scroll container, when mounted
 */
export function getNotificationScrollContainer(): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    "[data-notification-scroll-container]",
  );
}

/**
 * Build a stable identity for one notification table view.
 *
 * @param date - Selected roll-up date
 * @param tab - Active document-type tab
 * @returns Cache key for the date/tab combination
 */
export function notificationViewKey(date: string, tab: string): string {
  return `${date}::${tab}`;
}

/**
 * Build a cache key for one server-filtered, paginated notification result set.
 *
 * @param date - Selected roll-up date
 * @param tab - Active document-type tab
 * @param filtersKey - Stable serialized applied-filter key
 * @param parts - Optional search, sort, and page dimensions
 * @returns Cache key for one notification query
 */
export function notificationQueryKey(
  date: string,
  tab: string,
  filtersKey: string,
  parts: NotificationQueryKeyParts = {},
): string {
  const search = (parts.searchQuery ?? "").trim().toLowerCase();
  const sortKey = parts.sortKey ?? "";
  const sortDirection = parts.sortDirection ?? "asc";
  const page = parts.page ?? 0;
  return `${notificationViewKey(date, tab)}::${filtersKey}::q=${search}::sort=${sortKey}:${sortDirection}::page=${page}`;
}

/**
 * Read the full cached page — rows plus every count the footer renders.
 *
 * @param key - Query key from {@link notificationQueryKey}
 * @returns Cached page, or `undefined` before the first successful load
 */
export function getCachedNotificationPage(
  key: string,
): NotificationPageCache | undefined {
  return pageByQuery.get(key);
}

/**
 * Cache one page of results after a successful notification query.
 *
 * @param key - Query key from {@link notificationQueryKey}
 * @param page - Rows and the counts describing them
 * @returns Nothing
 */
export function setCachedNotificationPage(
  key: string,
  page: NotificationPageCache,
): void {
  pageByQuery.set(key, page);
}

/**
 * Read patient rows previously fetched for a notification query.
 *
 * @param key - Query key from {@link notificationQueryKey}
 * @returns Cached patient rows, or `undefined` before the first successful load
 */
export function getCachedNotificationPatients(
  key: string,
): Patient[] | undefined {
  return pageByQuery.get(key)?.patients;
}

/**
 * Replace only the rows of a cached page, leaving its counts untouched.
 *
 * Used by the optimistic read/unread updates, which reorder nothing and add no
 * rows — so the record range stays valid and must survive the write. Writing an
 * entry for an uncached query would strand rows with zeroed counts, so a miss is
 * ignored rather than seeded.
 *
 * @param key - Query key from {@link notificationQueryKey}
 * @param patients - Transformed patient rows
 * @returns Nothing
 */
export function setCachedNotificationPatients(
  key: string,
  patients: Patient[],
): void {
  const cached = pageByQuery.get(key);
  if (!cached) return;
  pageByQuery.set(key, { ...cached, patients });
}

/**
 * Read personalized unread report counts cached for a date.
 *
 * @param date - Selected roll-up date
 * @returns Cached unread counts, or `undefined` before a successful request
 */
export function getCachedUnreadDocumentCounts(
  date: string,
): UnreadDocumentCounts | undefined {
  return unreadCountsByDate.get(date);
}

/**
 * Cache personalized unread report counts for a date.
 *
 * @param date - Selected roll-up date
 * @param counts - Frontend document category to unread report count
 * @returns Nothing
 */
export function setCachedUnreadDocumentCounts(
  date: string,
  counts: UnreadDocumentCounts,
): void {
  unreadCountsByDate.set(date, counts);
}

/**
 * Read the last scroll offset for a date/tab view.
 *
 * @param key - View key from {@link notificationViewKey}
 * @returns Saved scroll offset, defaulting to the top
 */
export function getNotificationScrollTop(key: string): number {
  return scrollByView.get(key) ?? 0;
}

/**
 * Save the scroll offset for a date/tab view.
 *
 * @param key - View key from {@link notificationViewKey}
 * @param scrollTop - Vertical scroll offset in pixels
 */
export function setNotificationScrollTop(key: string, scrollTop: number): void {
  scrollByView.set(key, scrollTop);
}

/**
 * Read the remembered table sort for a notification view.
 *
 * @param key - View key from {@link notificationViewKey}
 * @returns Saved column/direction sort, or `undefined` before the first sort
 */
export function getNotificationSort(key: string): NotificationSort | undefined {
  return sortByView.get(key);
}

/**
 * Save the table sort for a notification view so browser Back can restore it.
 *
 * @param key - View key from {@link notificationViewKey}
 * @param sort - Column key and direction to remember
 * @returns Nothing
 */
export function setNotificationSort(key: string, sort: NotificationSort): void {
  sortByView.set(key, sort);
}
